// Un seul téléchargement et un seul décodage par banque d'échantillons, pour
// toute la page. Avant ce module, les m4a du piano partaient quatre fois : une
// pour le piano de tap (notePiano), deux pour la banque d'accords
// (chordInstruments construit un sampler d'accords et un sampler de nappe), une
// pour le bouton Ear du manche (NeckLayers). Même chose, en double, pour la
// guitare.
//
// Ce module ne possède que les `ToneAudioBuffer`. Chaque consommateur garde son
// propre `Tone.Sampler`, avec sa réverbération, son volume, sa relâche et son
// bus de mix : le partage porte sur la mémoire et le décodage, pas sur le son.
//
// PIÈGE : ne jamais bâtir un Sampler sur des buffers pas encore chargés.
// `ToneAudioBuffer.set` réécrit le `onload` d'un buffer non chargé, or c'est
// précisément ce callback qui décrémente le compteur de `ToneAudioBuffers` :
// le `onload` de la banque partagée ne tomberait jamais. On attend donc `ready`
// avant de construire quoi que ce soit.

import * as Tone from 'tone';
import { Capacitor } from '@capacitor/core';

// Web : sous /app/ via PUBLIC_URL. Natif : chemin relatif (bundle Capacitor).
const audioBase = Capacitor.isNativePlatform() ? 'audio/' : `${process.env.PUBLIC_URL}/audio/`;

export const SAMPLE_BANKS = {
  piano: {
    baseUrl: `${audioBase}piano/`,
    urls: {
      'A0': 'A0.flac', 'C1': 'C1.flac', 'D#1': 'Ds1.flac', 'F#1': 'Fs1.flac',
      'A1': 'A1.flac', 'C2': 'C2.flac', 'D#2': 'Ds2.flac', 'F#2': 'Fs2.flac',
      'A2': 'A2.flac', 'C3': 'C3.flac', 'D#3': 'Ds3.flac', 'F#3': 'Fs3.flac',
      'A3': 'A3.flac', 'C4': 'C4.flac', 'D#4': 'Ds4.flac', 'F#4': 'Fs4.flac',
      'A4': 'A4.flac', 'C5': 'C5.flac', 'D#5': 'Ds5.flac', 'F#5': 'Fs5.flac',
      'A5': 'A5.flac', 'C6': 'C6.flac'
    }
  },
  guitar: {
    baseUrl: `${audioBase}guitar/`,
    urls: {
      'E2': 'E2.flac', 'A2': 'A2.flac', 'C3': 'C3.flac', 'E3': 'E3.flac', 'A3': 'A3.flac',
      'C4': 'C4.flac', 'E4': 'E4.flac', 'A4': 'A4.flac', 'C5': 'C5.flac', 'D5': 'D5.flac'
    }
  }
};

// nom -> { buffers, ready: Promise<ToneAudioBuffers> }
const banks = {};

/**
 * Démarre (ou retrouve) le chargement d'une banque. Idempotent : les appels
 * suivants rendent la même entrée, donc la même promesse.
 */
export const loadSampleBank = (name) => {
  if (banks[name]) return banks[name];
  const cfg = SAMPLE_BANKS[name];
  const entry = { buffers: null, ready: null };
  entry.ready = new Promise((resolve, reject) => {
    entry.buffers = new Tone.ToneAudioBuffers({
      urls: cfg.urls,
      baseUrl: cfg.baseUrl,
      onload: () => resolve(entry.buffers),
      onerror: (error) => {
        // Échec réseau ou décodage : on jette la banque et on retire l'entrée,
        // pour qu'un prochain appel reparte de zéro (le piano du manche relance
        // après une panne). Personne n'a encore bâti de sampler dessus, puisque
        // c'est `ready` qui autorise la construction : le dispose est sûr.
        if (banks[name] === entry) delete banks[name];
        try { entry.buffers.dispose(); } catch (e) {
          // déjà libérée
        }
        reject(error);
      },
    });
  });
  // Un consommateur peut arriver après coup : sans cette prise en charge, un
  // échec sans écouteur remonterait en rejet non traité.
  entry.ready.catch(() => {});
  banks[name] = entry;
  return entry;
};

// La banque est-elle jouable tout de suite ? Synchrone, sans rien déclencher.
export const isSampleBankLoaded = (name) => Boolean(
  banks[name] && banks[name].buffers && banks[name].buffers.loaded
);

/**
 * Table `note -> ToneAudioBuffer` prête pour un `Tone.Sampler`. Tone enveloppe
 * chaque buffer déjà décodé sans re-télécharger ni re-décoder quoi que ce soit.
 * À n'appeler qu'une fois la banque chargée.
 */
export const sampleUrls = (name) => {
  const entry = banks[name];
  const urls = {};
  Object.keys(SAMPLE_BANKS[name].urls).forEach((note) => {
    urls[note] = entry.buffers.get(note);
  });
  return urls;
};
